import { Controller } from "@hotwired/stimulus"

// Filters the backoffice sidebar menu as the user types. Sections are opened by toggling
// Bootstrap's `show` class directly, not the collapse API, so the forced state never reaches
// the localStorage persistence in side-navigation-session.js.
export default class extends Controller {
  static targets = ["input", "section", "subgroup", "link", "divider", "excluded", "empty"]

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    if (query === "") {
      this.reset()
      return
    }

    this.openState ??= this.collapses.map(collapse => [collapse, collapse.classList.contains("show")])

    const matches = element => element?.dataset.navFilterName.toLowerCase().includes(query)
    this.linkTargets.forEach(link => {
      const subgroup = link.closest('[data-nav-filter-target~="subgroup"]')
      const section = link.closest('[data-nav-filter-target~="section"]')
      link.hidden = !(matches(link) || matches(subgroup) || matches(section))
    })

    this.subgroupTargets.forEach(subgroup => { subgroup.hidden = !this.hasVisibleLink(subgroup) })
    this.sectionTargets.forEach(section => { section.hidden = !this.hasVisibleLink(section) })
    this.collapses.forEach(collapse => this.setOpen(collapse, this.hasVisibleLink(collapse)))
    this.excludedTargets.forEach(excluded => { excluded.hidden = true })
    this.dividerTargets.forEach((divider, index) => { divider.hidden = !this.hasVisibleSectionAfter(divider, this.dividerTargets[index + 1]) })
    this.emptyTarget.hidden = this.sectionTargets.some(section => !section.hidden)
  }

  navigate(event) {
    if (event.key === "Escape") {
      this.inputTarget.value = ""
      this.reset()
    } else if (event.key === "Enter" && this.openState) {
      event.preventDefault()
      this.linkTargets.find(link => !link.hidden)?.querySelector("a")?.click()
    }
  }

  reset() {
    if (!this.openState) return

    const targets = [...this.linkTargets, ...this.subgroupTargets, ...this.sectionTargets, ...this.dividerTargets, ...this.excludedTargets]
    targets.forEach(target => { target.hidden = false })
    this.emptyTarget.hidden = true
    this.openState.forEach(([collapse, open]) => this.setOpen(collapse, open))
    this.openState = null
  }

  get collapses() {
    return this.sectionTargets.flatMap(section => [...section.querySelectorAll(".sidebar-nav.collapse")])
  }

  hasVisibleLink(container) {
    return this.linkTargets.some(link => !link.hidden && container.contains(link))
  }

  hasVisibleSectionAfter(divider, nextDivider) {
    const follows = (first, second) => first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING
    return this.sectionTargets.some(section => !section.hidden && follows(divider, section) && (!nextDivider || follows(section, nextDivider)))
  }

  setOpen(collapse, open) {
    collapse.classList.toggle("show", open)
    const header = collapse.previousElementSibling
    if (!header) return

    header.setAttribute("aria-expanded", String(open))
    const caret = header.querySelector(".toggle-icon")
    if (caret) caret.style.transform = open ? "rotate(180deg)" : ""
  }
}
